import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  collection,
  getFirestore,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import Card from 'components/card/Card';
import InstructorFullClassCalendar from 'components/calendar/InstructorFullClassCalendar';
import { AuthContext } from 'contexts/AuthContext';
import { app } from 'lib/firebase';
import InstructorClassDetailsModal from './components/InstructorClassDetailsModal';
import {
  buildAssignedInstructorClassesQuery,
  sortAssignedClassesByDate,
} from 'utils/instructorClassQueries';
import { getVisibleInstructorCalendarClasses } from 'utils/instructorCalendarDisplay';

const db = getFirestore(app);

export default function InstructorCalendar() {
  const { userDocId } = useContext(AuthContext);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const brandColor = useColorModeValue('brand.500', 'brand.400');

  useEffect(() => {
    if (!userDocId) {
      setClasses([]);
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);
    const assignedQuery = buildAssignedInstructorClassesQuery({
      db,
      collection,
      query,
      where,
      instructorId: userDocId,
    });

    const unsubscribe = onSnapshot(
      assignedQuery,
      (snapshot) => {
        const nextClasses = snapshot.docs.map((classDoc) => ({
          id: classDoc.id,
          ...classDoc.data(),
        }));
        setClasses(sortAssignedClassesByDate(getVisibleInstructorCalendarClasses(nextClasses)));
        setIsLoading(false);
      },
      (error) => {
        console.error('Error loading assigned instructor classes:', error);
        setIsLoading(false);
      }
    );

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [userDocId]);

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="instructor-calendar-page">
      <Box id="calendar-full-widget">
        {isLoading ? (
          <Card p="28px">
            <Flex align="center" gap="12px">
              <Spinner color={brandColor} />
              <Text color={mutedText}>Loading your classes...</Text>
            </Flex>
          </Card>
        ) : (
          <InstructorFullClassCalendar
            classes={classes}
            onClassClick={(classData) => setSelectedClass(classData)}
          />
        )}
      </Box>

      <InstructorClassDetailsModal
        isOpen={Boolean(selectedClass)}
        onClose={() => setSelectedClass(null)}
        classData={selectedClass}
      />
    </Box>
  );
}
